import {
  faArrowUpRightFromSquare,
  faCalendarAlt,
  faCheck,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useState } from "react";
import { Link } from "react-router-dom";
import styled, { keyframes } from "styled-components/macro";

// ─── Styled components ────────────────────────────────────────────────────────

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.sm};
  animation: ${fadeUp} 0.35s ease both;
`;

const Card = styled.div`
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  background: ${({ theme }) => theme.colors.primaryBackground};
  padding: ${({ theme }) => theme.spacing.md};
  box-shadow: ${({ theme }) => theme.boxShadow.accountCard};
`;

const CardLabel = styled.p`
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.body};
  margin: 0 0 12px;
`;

const EventRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing.sm};
`;

const IconCircle = styled.div`
  width: ${({ small }) => (small ? "28px" : "36px")};
  height: ${({ small }) => (small ? "28px" : "36px")};
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.altBackground};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: ${({ theme }) => theme.colors.heading};
  font-size: ${({ small }) => (small ? "12px" : "14px")};
`;

const EventTitle = styled.p`
  font-size: ${({ small }) => (small ? "13px" : "14px")};
  font-weight: ${({ small }) => (small ? "600" : "700")};
  color: ${({ theme }) => theme.colors.heading};
  margin: 0 0 2px;
  display: -webkit-box;
  -webkit-line-clamp: ${({ expanded }) => (expanded ? "unset" : "2")};
  -webkit-box-orient: vertical;
  overflow: hidden;
  cursor: default;
`;

const EventMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.body};
`;

const Dot = styled.span`
  color: ${({ theme }) => theme.colors.body};
`;

const EventDescription = styled.p`
  margin: 4px 0 0;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.body};
  display: -webkit-box;
  -webkit-line-clamp: ${({ expanded }) => (expanded ? "unset" : "2")};
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const ReadMoreButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  font-size: 11px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.heroBlue};
  cursor: pointer;
  &:hover {
    text-decoration: underline;
  }
`;

const EventActions = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 6px;
  gap: ${({ theme }) => theme.spacing.sm};
`;

const EventList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.md};
`;

const ViewAllLink = styled(Link)`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.heroBlue};
  text-decoration: none;
  padding: ${({ theme }) => theme.spacing.sm};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  transition: background 0.15s;
  &:hover {
    background: ${({ theme }) => theme.colors.altBackground};
    text-decoration: none;
  }
`;

/* const SeeMoreButton = styled.button`
  margin-top: ${({ theme }) => theme.spacing.sm};
  width: 100%;
  border: none;
  background: transparent;
  padding: 8px ${({ theme }) => theme.spacing.md};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: 13px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.heroPink};
  cursor: pointer;
  transition: background 0.15s;
  &:hover {
    background: ${({ theme }) => theme.colors.altBackground};
  }
`; */

const EventInfoLink = styled.a`
  font-size: 12px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.heroBlue};
  text-decoration: none;
  white-space: nowrap;
  &:hover {
    text-decoration: ${({ registered }) => (registered ? "none" : "underline")};
  }
`;

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * GPDS-892/904 — Upcoming event module
 * Shows the next event and a list of upcoming events.
 * Driven by the `data` array from useEvents (each item has a `values` object).
 */
function parseEventDate(dateStr) {
  if (!dateStr) return null;
  // e.g. "Wed, 11 Feb 2026 @ 12pm AEST"
  try {
    const match = dateStr.match(
      /(\d{1,2})\s+(\w+)\s+(\d{4})\s*@\s*(\d{1,2})(am|pm)/i
    );
    if (!match) return null;
    const [, day, month, year, hour, meridiem] = match;
    const h = parseInt(hour, 10);
    const hour24 =
      meridiem.toLowerCase() === "pm" && h !== 12
        ? h + 12
        : meridiem.toLowerCase() === "am" && h === 12
        ? 0
        : h;
    return new Date(
      `${day} ${month} ${year} ${String(hour24).padStart(2, "0")}:00:00`
    );
  } catch {
    return null;
  }
}

function formatEventDate(date) {
  if (!date) return "";
  const month = date.toLocaleDateString("en-US", { month: "long" });
  const day = date.getDate();
  const time = date.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
  return `${month} ${day} · ${time}`;
}

export default function UpcomingEventModule({ data }) {
  const now = new Date();
  const [expandedId, setExpandedId] = useState(null);

  const sorted = (Array.isArray(data) ? data : [])
    .map((e) => ({ ...e, _date: parseEventDate(e.values?.date) }))
    .filter((e) => e._date)
    .sort((a, b) => a._date - b._date);

  const nextIdx = sorted.findIndex((e) => e._date >= now);
  const next = nextIdx !== -1 ? sorted[nextIdx] : sorted[sorted.length - 1];

  if (!next) return null;

  const nextEvent = next.values;
  const upcomingEvents = sorted
    .slice(nextIdx !== -1 ? nextIdx + 1 : sorted.length)
    .slice(0, 3);
  return (
    <Wrapper>
      {/* Your Next Event */}
      <Card>
        <CardLabel>Your Next Event</CardLabel>
        <EventRow>
          <IconCircle>
            <FontAwesomeIcon icon={faCalendarAlt} />
          </IconCircle>
          <div>
            <EventTitle
              title={nextEvent.title}
              expanded={expandedId === next.id}
            >
              {nextEvent.title}
            </EventTitle>
            <EventMeta>
              <b>{formatEventDate(next._date)}</b>
              {nextEvent.via && (
                <>
                  <Dot>·</Dot>
                  <b>{nextEvent.via}</b>
                </>
              )}
            </EventMeta>
            {nextEvent.description && (
              <EventDescription expanded={expandedId === next.id}>
                {nextEvent.description}
              </EventDescription>
            )}
            {(nextEvent.description || nextEvent.registration_url) && (
              <EventActions>
                {nextEvent.registration_url ? (
                  <EventInfoLink
                    href={nextEvent.registration_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    registered={nextEvent.is_registered}
                  >
                    {nextEvent.is_registered ? (
                      <>
                        <span>Registered </span>
                        <FontAwesomeIcon
                          icon={faCheck}
                          style={{ fontSize: 14 }}
                        />
                      </>
                    ) : (
                      <>
                        <span>Register </span>
                        <FontAwesomeIcon
                          icon={faArrowUpRightFromSquare}
                          style={{ fontSize: 8 }}
                        />
                      </>
                    )}
                  </EventInfoLink>
                ) : (
                  <span />
                )}

                {nextEvent.description ? (
                  <ReadMoreButton
                    onClick={() =>
                      setExpandedId(expandedId === next.id ? null : next.id)
                    }
                  >
                    {expandedId === next.id ? "Show less" : "Read more"}
                  </ReadMoreButton>
                ) : (
                  <span />
                )}
              </EventActions>
            )}
          </div>
        </EventRow>
      </Card>

      {upcomingEvents.length > 0 && (
        <Card>
          <CardLabel>Upcoming Events</CardLabel>
          <EventList>
            {upcomingEvents.map((event) => (
              <EventRow key={event.id}>
                <IconCircle small>
                  <FontAwesomeIcon icon={faCalendarAlt} />
                </IconCircle>
                <div>
                  <EventTitle
                    small
                    title={event.values.title}
                    expanded={expandedId === event.id}
                  >
                    {event.values.title}
                  </EventTitle>
                  <EventMeta>
                    <b>{formatEventDate(event._date)}</b>
                    {event.values.via && (
                      <>
                        <Dot>·</Dot>
                        <b>{event.values.via}</b>
                      </>
                    )}
                  </EventMeta>
                  {event.values.description && (
                    <EventDescription expanded={expandedId === event.id}>
                      {event.values.description}
                    </EventDescription>
                  )}
                  {(event.values.description ||
                    event.values.registration_url) && (
                    <EventActions>
                      {event.values.registration_url &&
                      expandedId === event.id ? (
                        <EventInfoLink
                          href={event.values.registration_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          registered={event.values.is_registered}
                        >
                          {event.values.is_registered ? (
                            <>
                              <span>Registered </span>
                              <FontAwesomeIcon
                                icon={faCheck}
                                style={{ fontSize: 14 }}
                              />
                            </>
                          ) : (
                            <>
                              <span>Register </span>
                              <FontAwesomeIcon
                                icon={faArrowUpRightFromSquare}
                                style={{ fontSize: 8 }}
                              />
                            </>
                          )}
                        </EventInfoLink>
                      ) : (
                        <span />
                      )}
                      {event.values.description ? (
                        <ReadMoreButton
                          onClick={() =>
                            setExpandedId(
                              expandedId === event.id ? null : event.id
                            )
                          }
                        >
                          {expandedId === event.id ? "Show less" : "Read more"}
                        </ReadMoreButton>
                      ) : (
                        <span />
                      )}
                    </EventActions>
                  )}
                </div>
              </EventRow>
            ))}
          </EventList>
          {/* <SeeMoreButton>See more →</SeeMoreButton> */}
        </Card>
      )}
      <ViewAllLink to="/events">View all events →</ViewAllLink>
    </Wrapper>
  );
}
